const DEFAULT_IMAGE_OVERLAY = '#0000008c'

function lookupLocalValue(localVariables, name) {
    if (!localVariables || !name) return ''
    const entry = localVariables[name]
    if (entry && typeof entry === 'object' && Object.prototype.hasOwnProperty.call(entry, 'value')) {
        return entry.value == null ? '' : String(entry.value)
    }
    return ''
}

function isTransparentColor(color) {
    return String(color || '').trim().toLowerCase() === 'transparent'
}

function hasExplicitAlpha(color) {
    if (!color || isTransparentColor(color)) return false
    const trimmed = String(color).trim()
    if (/^rgba\(/i.test(trimmed)) return true
    const hex = trimmed.replace('#', '')
    return hex.length === 8 && /^[0-9a-f]+$/i.test(hex)
}

function toHexByte(value) {
    return Math.round(Math.min(255, Math.max(0, Number(value)))).toString(16).padStart(2, '0')
}

function channelsToHex8(r, g, b, a = 1) {
    const alpha = Number(a)
    const alphaByte = Number.isNaN(alpha) ? 255 : Math.round(Math.min(1, Math.max(0, alpha)) * 255)
    return `#${toHexByte(r)}${toHexByte(g)}${toHexByte(b)}${toHexByte(alphaByte)}`
}

function colorToHex8(color) {
    if (!color || color === 'transparent') return null
    const trimmed = String(color).trim()
    if (trimmed.indexOf('global_.') > 0) return trimmed
    const rgbaMatch = trimmed.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([0-9.]+))?\s*\)$/i)
    if (rgbaMatch) {
        const alpha = rgbaMatch[4] === undefined ? 1 : Number(rgbaMatch[4])
        return channelsToHex8(rgbaMatch[1], rgbaMatch[2], rgbaMatch[3], alpha)
    }
    const hex = trimmed.replace('#', '')
    if (hex.length === 3 && /^[0-9a-f]+$/i.test(hex)) {
        const full = hex
            .split('')
            .map((character) => character + character)
            .join('')
        return `#${full}ff`
    }
    if (hex.length === 6 && /^[0-9a-f]+$/i.test(hex)) return `#${hex}ff`
    if (hex.length === 8 && /^[0-9a-f]+$/i.test(hex)) return `#${hex}`
    return null
}

function overlayToRgba(color) {
    if (!color || color === 'transparent') return null
    const trimmed = String(color).trim()
    const rgbaMatch = trimmed.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([0-9.]+))?\s*\)$/i)
    if (rgbaMatch) {
        const alpha = rgbaMatch[4] === undefined ? 1 : Number(rgbaMatch[4])
        return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${alpha})`
    }
    const hex = trimmed.replace('#', '')
    if (hex.length !== 3 && hex.length !== 6 && hex.length !== 8) return null
    const full =
        hex.length === 3
            ? hex
                  .split('')
                  .map((character) => character + character)
                  .join('')
            : hex
    const red = parseInt(full.slice(0, 2), 16)
    const green = parseInt(full.slice(2, 4), 16)
    const blue = parseInt(full.slice(4, 6), 16)
    const alpha = full.length === 8 ? Math.round((parseInt(full.slice(6, 8), 16) / 255) * 100) / 100 : 1
    if ([red, green, blue, alpha].some(Number.isNaN)) return null
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}

function colorToOverlayValue(color) {
    if (isTransparentColor(color)) return 'transparent'
    if (!color) return DEFAULT_IMAGE_OVERLAY
    if (typeof color === 'string' && color.indexOf('global_.') > 0) return color
    return colorToHex8(color) || DEFAULT_IMAGE_OVERLAY
}

function composeBackgroundImage(imageUrl, overlayColor) {
    if (!imageUrl) return ''
    const urlPart = `url(${imageUrl})`
    if (isTransparentColor(overlayColor)) return urlPart
    const source = hasExplicitAlpha(overlayColor) ? overlayColor : DEFAULT_IMAGE_OVERLAY
    const rgba = overlayToRgba(source) || overlayToRgba(DEFAULT_IMAGE_OVERLAY)
    if (!rgba) return urlPart
    const alphaMatch = rgba.match(/,\s*([0-9.]+)\)$/)
    const alpha = alphaMatch ? Number(alphaMatch[1]) : 1
    if (alpha === 0) return urlPart
    return `linear-gradient(${rgba}, ${rgba}), ${urlPart}`
}

function resolveBackgroundPosition(position, x, y) {
    if (!position) return ''
    if (position === 'custom') return `${x || '0px'} ${y || '0px'}`
    return position
}

function resolveBackgroundSize(size, width, height) {
    if (!size) return ''
    if (size === 'custom') return `${width || 'auto'} ${height || 'auto'}`
    return size
}

function resolveMaybeGlobal(value, globalVariables) {
    if (value && typeof value === 'string' && value.indexOf('global_.') > 0 && globalVariables) {
        const key = value.split('_.')[1]
        if (globalVariables[key] && globalVariables[key].value != null) {
            return String(globalVariables[key].value)
        }
    }
    return value
}

function resolveBgPrefixFromBinding(binding) {
    if (!binding || typeof binding !== 'string') return null
    const numbered = binding.match(/_localVariables_\.(column\d+)Bg/)
    if (numbered) return numbered[1]
    if (binding.indexOf('_localVariables_.rowBg') > -1) return 'row'
    if (binding.indexOf('_localVariables_.columnBg') > -1) return 'column'
    return null
}

function applyLandingBackgroundCssVar(cssVar, rawValue, localVariables, globalVariables, localVarBinding) {
    if (cssVar.indexOf('--row-justify-items') === 0) {
        const alignment = String(rawValue || '').trim()
        if (!alignment || alignment === 'center' || alignment === 'end') return rawValue
        return 'stretch'
    }
    const prefixes = [
        { prefix: 'row', varPrefix: '--row-bg' },
        { prefix: 'column', varPrefix: '--column-bg' }
    ]
    const found = prefixes.find((candidate) => cssVar.indexOf(candidate.varPrefix) === 0)
    if (!found) return rawValue

    let bgPrefix = found.prefix
    const bindingPrefix = resolveBgPrefixFromBinding(localVarBinding)
    if (bindingPrefix && bindingPrefix.indexOf('column') === 0) bgPrefix = bindingPrefix

    let breakpoint = 'desktop'
    let kind = cssVar.slice(found.varPrefix.length)
    if (kind.endsWith('-tablet')) {
        breakpoint = 'tablet'
        kind = kind.slice(0, -'-tablet'.length)
    } else if (kind.endsWith('-mobile')) {
        breakpoint = 'mobile'
        kind = kind.slice(0, -'-mobile'.length)
    }

    const suffix = breakpoint === 'tablet' ? 'Tablet' : breakpoint === 'mobile' ? 'Mobile' : ''
    if (cssVar === `${found.varPrefix}-image`) {
        const overlayKey = bgPrefix === 'row' ? 'rowBg' : bgPrefix + 'Bg'
        const overlay = resolveMaybeGlobal(lookupLocalValue(localVariables, overlayKey), globalVariables)
        return composeBackgroundImage(rawValue, overlay)
    }
    if (kind === '-position') {
        return resolveBackgroundPosition(
            rawValue,
            lookupLocalValue(localVariables, bgPrefix + 'BgPositionX' + suffix),
            lookupLocalValue(localVariables, bgPrefix + 'BgPositionY' + suffix)
        )
    }
    if (kind === '-size') {
        return resolveBackgroundSize(
            rawValue,
            lookupLocalValue(localVariables, bgPrefix + 'BgSizeW' + suffix),
            lookupLocalValue(localVariables, bgPrefix + 'BgSizeH' + suffix)
        )
    }
    return rawValue
}

function isEmptyPropValue(value) {
    return value == null || value === ''
}

function isGlobalReference(value) {
    return typeof value === 'string' && value.indexOf('_global_.') > -1
}

function getLandingColumnCount(element) {
    if (!element || typeof element !== 'object') return 0
    let dataColumns = null
    function findDataColumns(node) {
        if (!node || typeof node !== 'object' || dataColumns != null) return
        const columns = node.props && node.props['data-columns'] && node.props['data-columns'].value
        if (columns != null && columns !== '') dataColumns = String(columns)
        if (Array.isArray(node.elements)) node.elements.forEach(findDataColumns)
    }
    findDataColumns(element)
    if (dataColumns) {
        if (dataColumns.indexOf('/') > -1) return dataColumns.split('/').length
        const parsed = parseInt(dataColumns, 10)
        if (!Number.isNaN(parsed) && parsed > 0) return parsed
    }
    let canvasColumns = 0
    function countCanvasColumns(node) {
        if (!node || typeof node !== 'object') return
        if (node.dragDrop === 'canvasColumn') canvasColumns += 1
        if (Array.isArray(node.elements)) node.elements.forEach(countCanvasColumns)
    }
    countCanvasColumns(element)
    return canvasColumns
}

function migrateSharedColumnBackgroundProps(props, columnCount) {
    if (!props || columnCount <= 1) return props
    const migrated = { ...props }
    Object.keys(props).forEach((sharedKey) => {
        if (sharedKey.indexOf('columnBg') !== 0) return
        if (isEmptyPropValue(props[sharedKey])) return
        if (sharedKey === 'columnBg' && isGlobalReference(props[sharedKey])) return
        for (let columnNumber = 1; columnNumber <= columnCount; columnNumber += 1) {
            const perColumnKey = sharedKey.replace(/^column/, 'column' + columnNumber)
            if (isEmptyPropValue(migrated[perColumnKey])) migrated[perColumnKey] = props[sharedKey]
        }
    })
    return migrated
}

function isDynamicImageValue(value) {
    return typeof value === 'string' && value.indexOf('/api/dynamicimage?') > -1
}

export {
    DEFAULT_IMAGE_OVERLAY,
    overlayToRgba,
    hasExplicitAlpha,
    channelsToHex8,
    colorToHex8,
    isTransparentColor,
    colorToOverlayValue,
    composeBackgroundImage,
    resolveBackgroundPosition,
    resolveBackgroundSize,
    applyLandingBackgroundCssVar,
    getLandingColumnCount,
    migrateSharedColumnBackgroundProps,
    isDynamicImageValue
}
